/**
 * ====================================================================
 * FIKO TEKNIK RESIM TOOLBAR - CATIA TARZI ARAÇ ÇUBUĞU
 * ====================================================================
 *
 * FikoFloatingToolbar kullanan teknik resim araç çubuğu.
 * Ana 3D viewer ile aynı UI tarzında.
 *
 * Araçlar:
 * - Görünüm ekleme (Ön, Üst, Sağ, Sol, Alt, Arka, İzometrik)
 * - Kesit görünümü
 * - Detay görünümü
 * - Grid toggle
 * - Snap toggle
 *
 * @author FikoYT CAD Team
 * @version 3.0.0
 */

import { Component, OnInit, OnDestroy, Input, Inject, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FikoFloatingToolbarComponent } from '../../../fiko-floating-toolbar/fiko-floating-toolbar.component';
import { FikoFloatingToolbarService } from '../../../fiko-floating-toolbar/fiko-floating-toolbar.service';
import { FikoTeknikResimModu, TeknikResimModuDurumu } from '../../mode/FikoTeknikResimModu';
import { ViewType, PaperSize, PaperOrientation } from '../../core/types';
import { Subscription } from 'rxjs';

/**
 * FikoTeknikResimToolbarComponent - Teknik Resim Araç Çubuğu
 */
@Component({
    selector: 'fikoyt-teknik-resim-toolbar',
    standalone: true,
    imports: [CommonModule, FikoFloatingToolbarComponent],
    template: `
<!-- Ana Teknik Resim Toolbar - Pill Şeklinde -->
<fikoyt-floating-toolbar
    [toolbarId]="toolbarId"
    [baslik]="'Teknik Resim'"
    [simge]="'drafting compass'"
    [simgeTipi]="'icon'"
    [baslikGorunur]="true"
    [handleGorunur]="true"
    [kapatButonuGorunur]="true"
    [minimizeButonuGorunur]="true"
    [ozelSinif]="'teknik-resim-toolbar'"
    [gorunur]="false"
    [baslangicPozisyonu]="{ top: 100, left: 20 }"
    (kapandi)="onToolbarClosed()">

    <div class="teknik-resim-tools">
        <!-- MOD KONTROL -->
        <div class="tool-group">
            <button class="circular ui icon mini button"
                    [class.green]="modAktif"
                    [class.grey]="!modAktif"
                    (click)="modToggle()"
                    [title]="modAktif ? 'Teknik Resim Modunu Kapat' : 'Teknik Resim Modunu Aç'">
                <i class="icon" [class.play]="!modAktif" [class.stop]="modAktif"></i>
            </button>
        </div>

        <div class="separator"></div>

        <!-- GÖRÜNÜM ARAÇLARI -->
        <div class="tool-group" [class.disabled]="!modAktif">
            <!-- Ön Görünüm -->
            <button class="circular ui icon blue mini button"
                    (click)="gorunumEkle('FRONT')"
                    [disabled]="!modAktif"
                    title="Ön Görünüm">
                <i class="icon square"></i>
            </button>

            <!-- Üst Görünüm -->
            <button class="circular ui icon blue mini button"
                    (click)="gorunumEkle('TOP')"
                    [disabled]="!modAktif"
                    title="Üst Görünüm">
                <i class="icon square outline"></i>
            </button>

            <!-- Sağ Görünüm -->
            <button class="circular ui icon blue mini button"
                    (click)="gorunumEkle('RIGHT')"
                    [disabled]="!modAktif"
                    title="Sağ Görünüm">
                <i class="icon caret square right"></i>
            </button>

            <!-- Sol Görünüm -->
            <button class="circular ui icon blue mini button"
                    (click)="gorunumEkle('LEFT')"
                    [disabled]="!modAktif"
                    title="Sol Görünüm">
                <i class="icon caret square left"></i>
            </button>

            <!-- Arka Görünüm -->
            <button class="circular ui icon teal mini button"
                    (click)="gorunumEkle('BACK')"
                    [disabled]="!modAktif"
                    title="Arka Görünüm">
                <i class="icon clone"></i>
            </button>

            <!-- Alt Görünüm -->
            <button class="circular ui icon teal mini button"
                    (click)="gorunumEkle('BOTTOM')"
                    [disabled]="!modAktif"
                    title="Alt Görünüm">
                <i class="icon window minimize"></i>
            </button>

            <!-- İzometrik -->
            <button class="circular ui icon purple mini button"
                    (click)="gorunumEkle('ISOMETRIC')"
                    [disabled]="!modAktif"
                    title="İzometrik Görünüm">
                <i class="icon cube"></i>
            </button>
        </div>

        <div class="separator"></div>

        <!-- ÖZEL GÖRÜNÜMLER -->
        <div class="tool-group" [class.disabled]="!modAktif">
            <!-- Kesit -->
            <button class="circular ui icon orange mini button"
                    (click)="kesitGorunumuEkle()"
                    [disabled]="!modAktif"
                    title="Kesit Görünümü">
                <i class="icon cut"></i>
            </button>

            <!-- Detay -->
            <button class="circular ui icon yellow mini button"
                    (click)="detayGorunumuEkle()"
                    [disabled]="!modAktif"
                    title="Detay Görünümü">
                <i class="icon search plus"></i>
            </button>
        </div>

        <div class="separator"></div>

        <!-- AYARLAR -->
        <div class="tool-group">
            <!-- Grid Toggle -->
            <button class="circular ui icon mini button"
                    [class.active]="gridGorunur"
                    [class.olive]="gridGorunur"
                    [class.grey]="!gridGorunur"
                    (click)="gridToggle()"
                    [disabled]="!modAktif"
                    title="Grid Aç/Kapat">
                <i class="icon th"></i>
            </button>

            <!-- Snap Toggle -->
            <button class="circular ui icon mini button"
                    [class.active]="snapAktif"
                    [class.olive]="snapAktif"
                    [class.grey]="!snapAktif"
                    (click)="snapToggle()"
                    [disabled]="!modAktif"
                    title="Snap Aç/Kapat">
                <i class="icon crosshairs"></i>
            </button>

            <!-- Kağıt Boyutu -->
            <button class="circular ui icon grey mini button"
                    (click)="kagitAyarlariAc()"
                    [disabled]="!modAktif"
                    title="Kağıt Ayarları">
                <i class="icon file outline"></i>
            </button>
        </div>

        <div class="separator"></div>

        <!-- TEMİZLE -->
        <div class="tool-group">
            <button class="circular ui icon red mini button"
                    (click)="tumGorunumleriSil()"
                    [disabled]="!modAktif"
                    title="Tüm Görünümleri Temizle">
                <i class="icon eraser"></i>
            </button>
        </div>
    </div>
</fikoyt-floating-toolbar>
    `,
    styles: [`
        .teknik-resim-tools {
            display: flex;
            flex-direction: row;
            gap: 4px;
            align-items: center;
            padding: 4px 0;
        }

        .tool-group {
            display: flex;
            flex-direction: row;
            gap: 4px;
            align-items: center;
        }

        .tool-group.disabled {
            opacity: 0.5;
            pointer-events: none;
        }

        .separator {
            width: 1px;
            height: 20px;
            background: rgba(255, 255, 255, 0.3);
            margin: 0 4px;
        }

        .teknik-resim-tools .button {
            transition: all 0.2s ease;
            position: relative;
        }

        .teknik-resim-tools .button.active {
            box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.8) inset !important;
            transform: scale(1.1);
        }

        .teknik-resim-tools .button:hover:not(:disabled) {
            transform: scale(1.15);
        }

        .teknik-resim-tools .button:disabled {
            opacity: 0.4;
            cursor: not-allowed;
        }

        /* Toolbar özel stilleri */
        :host ::ng-deep .teknik-resim-toolbar .fiko-toolbar-container {
            background: linear-gradient(135deg, rgba(0, 80, 80, 0.95) 0%, rgba(0, 40, 60, 0.95) 100%) !important;
            border: 1px solid rgba(0, 255, 255, 0.4) !important;
            box-shadow: 0px 0px 15px rgba(0, 255, 255, 0.6) !important;
        }

        :host ::ng-deep .teknik-resim-toolbar .fiko-toolbar-container:hover {
            box-shadow: 0px 0px 20px rgba(0, 255, 255, 0.8) !important;
        }

        :host ::ng-deep .teknik-resim-toolbar .fiko-toolbar-header {
            background: rgba(0, 255, 255, 0.1) !important;
            border-bottom: 1px solid rgba(0, 255, 255, 0.3) !important;
        }

        :host ::ng-deep .teknik-resim-toolbar .fiko-toolbar-header .baslik {
            color: rgba(0, 255, 255, 0.9) !important;
            text-shadow: 0 0 10px rgba(0, 255, 255, 0.5) !important;
        }
    `]
})
export class FikoTeknikResimToolbarComponent implements OnInit, OnDestroy {
    @Input() toolbarId: string = 'teknikResimTools';

    /** anaGosterim input - setter ile mod oluşturulur */
    private _anaGosterim?: any;
    @Input()
    set anaGosterim(value: any) {
        this._anaGosterim = value;
        // anaGosterim geldiğinde modu oluştur
        if (value && !this.teknikResimModu) {
            this.initMod();
        }
    }
    get anaGosterim(): any {
        return this._anaGosterim;
    }

    /** Teknik resim modu instance */
    private teknikResimModu: FikoTeknikResimModu | null = null;

    /** Durum subscription */
    private durumSub?: Subscription;

    /** Mod aktif mi? */
    modAktif: boolean = false;

    /** Grid görünür mü? */
    gridGorunur: boolean = true;

    /** Snap aktif mi? */
    snapAktif: boolean = true;

    constructor(
        private toolbarService: FikoFloatingToolbarService
    ) {}

    ngOnInit(): void {
        // anaGosterim zaten varsa modu oluştur
        if (this._anaGosterim && !this.teknikResimModu) {
            this.initMod();
        }
    }

    /**
     * Teknik resim modunu başlat
     */
    private initMod(): void {
        if (!this._anaGosterim || this.teknikResimModu) return;

        // console.log('[TeknikResimToolbar] Mod başlatılıyor, anaGosterim:', this._anaGosterim);
        this.teknikResimModu = new FikoTeknikResimModu(this._anaGosterim);

        // Durum değişikliklerini dinle
        this.durumSub = this.teknikResimModu.durumDegisti.subscribe(durum => {
            this.modAktif = durum.aktif;
            this.gridGorunur = durum.gridGorunur;
            this.snapAktif = durum.snapAktif;
        });
    }

    ngOnDestroy(): void {
        this.durumSub?.unsubscribe();
        this.teknikResimModu?.dispose();
    }

    /**
     * Toolbar'ı aç/kapat
     */
    public toggle(): void {
        const toolbar = this.toolbarService.bul(this.toolbarId);
        if (toolbar) {
            toolbar.toggle();
        }
    }

    /**
     * Toolbar'ı aç
     */
    public ac(): void {
        const toolbar = this.toolbarService.bul(this.toolbarId);
        if (toolbar) {
            toolbar.ac();
        }
    }

    /**
     * Toolbar'ı kapat
     */
    public kapa(): void {
        const toolbar = this.toolbarService.bul(this.toolbarId);
        if (toolbar) {
            toolbar.kapa();
        }
    }

    /**
     * Toolbar kapatıldığında
     */
    onToolbarClosed(): void {
        // Mod aktifse kapat
        if (this.modAktif) {
            this.teknikResimModu?.kapat();
        }
    }

    /**
     * Mod toggle
     */
    modToggle(): void {
        // Mod yoksa oluştur
        if (!this.teknikResimModu && this._anaGosterim) {
            this.initMod();
        }
        this.teknikResimModu?.toggle();
    }

    /**
     * Görünüm ekle
     */
    gorunumEkle(tip: string): void {
        if (!this.teknikResimModu?.aktifMi) return;
        this.teknikResimModu.gorunumEkle(tip as ViewType);
    }

    /**
     * Kesit görünümü ekle
     */
    kesitGorunumuEkle(): void {
        if (!this.teknikResimModu?.aktifMi) return;
        // TODO: Kesit düzlemi seçimi için dialog aç
        // console.log('[TeknikResimToolbar] Kesit görünümü - yakında');
    }

    /**
     * Detay görünümü ekle
     */
    detayGorunumuEkle(): void {
        if (!this.teknikResimModu?.aktifMi) return;
        // TODO: Detay bölgesi seçimi için dialog aç
        // console.log('[TeknikResimToolbar] Detay görünümü - yakında');
    }

    /**
     * Grid toggle
     */
    gridToggle(): void {
        this.teknikResimModu?.gridToggle();
    }

    /**
     * Snap toggle
     */
    snapToggle(): void {
        this.teknikResimModu?.snapToggle();
    }

    /**
     * Kağıt ayarlarını aç
     */
    kagitAyarlariAc(): void {
        // TODO: Kağıt ayarları dialog'u aç
        // console.log('[TeknikResimToolbar] Kağıt ayarları - yakında');
    }

    /**
     * Tüm görünümleri sil
     */
    tumGorunumleriSil(): void {
        if (!this.teknikResimModu?.aktifMi) return;

        const gorunumler = this.teknikResimModu.tumGorunumler();
        for (const g of gorunumler) {
            this.teknikResimModu.gorunumSil(g.id);
        }
    }

    /**
     * Modu al (dış erişim için)
     */
    getMod(): FikoTeknikResimModu | null {
        return this.teknikResimModu;
    }
}
